"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { BASE_URL, CDN_URL } from "../config/apiconfig";

/* ---------------- CONTEXT ---------------- */
const ReviewContext = createContext(null);

export const useCustomerReviews = () => {
  const context = useContext(ReviewContext);
  if (!context) {
    throw new Error("useCustomerReviews must be used within ReviewProvider");
  }
  return context;
};

/* ---------------- API ENDPOINT ---------------- */
const REVIEW_ENDPOINT = `${BASE_URL}/reviews`;

/* ---------------- NORMALIZER ---------------- */
const normalizeReview = (r) => ({
  id: String(r.id),
  userId: r.user_id,
  userName: r.user_name,
  productId: r.product_id,
  orderId: r.order_id,
  rating: Number(r.rating ?? 0),
  text: r.description ?? "",
  comment: r.description ?? "",
  description: r.description ?? "",
  status: r.status ?? "Active",
  createdAt: r.create_audit_time,
  image: r.image,
  userImage: r.user_image,
});

/* ---------------- PROVIDER ---------------- */
export const CustomerReviewProvider = ({ children }) => {
  const [orderReviews, setOrderReviews] = useState({}); // Store reviews by order ID
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("Authentication required. Please login again.");
    }
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  // Helper function to get current user
  const getCurrentUser = () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user?.id) {
      throw new Error("User information not found. Please login again.");
    }
    return user;
  };

  // Clear error function
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Fetch reviews for a specific order
  const fetchOrderReview = useCallback(async (orderId) => {
    if (!orderId) {
      console.log("No order ID provided for review fetch");
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const user = getCurrentUser();
      const headers = getAuthHeaders();

      console.log("Fetching reviews for order:", orderId, "User:", user.id);

      const response = await fetch(
        `${BASE_URL}/reviews/${user.id}/${orderId}`,
        { headers }
      );

      const data = await response.json();
      console.log(`Reviews API Response for order ${orderId}:`, data);

      if (data.status === "200" && data.data) {
        const reviews = data.data.map(review => ({
          ...normalizeReview(review),
          orderId: orderId // Ensure orderId is set correctly
        }));

        // Store reviews for this specific order
        setOrderReviews(prev => ({
          ...prev,
          [orderId]: reviews
        }));

        console.log("Fetched and stored reviews for order:", orderId, reviews);
        return reviews;
      } else {
        console.log("No reviews found for order:", orderId);
        // Store empty array for this order to indicate it was checked
        setOrderReviews(prev => ({
          ...prev,
          [orderId]: []
        }));
        return [];
      }
    } catch (err) {
      console.error("Fetch order review error:", err);

      if (err.message.includes("Authentication")) {
        setError("Session expired. Please login again.");
      } else {
        setError(`Failed to fetch review: ${err.message}`);
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a new review
  const createReview = useCallback(async (orderId, reviewData) => {
    setLoading(true);
    setError(null);

    try {
      const user = getCurrentUser();
      const headers = getAuthHeaders();

      const payload = {
        user_id: user.id.toString(),
        order_id: orderId.toString(),
        description: reviewData.comment || reviewData.description || '',
        rating: reviewData.rating.toString()
      };

      console.log("Creating review with payload:", payload);

      const response = await fetch(REVIEW_ENDPOINT, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      console.log("Create review API response:", data);

      if (data.status === 201 && data.data) {
        // Format the response data to match our review structure
        const newReviews = data.data.map(review => ({
          ...normalizeReview(review),
          orderId: orderId // Ensure orderId is set correctly
        }));

        // Update the order reviews state
        setOrderReviews(prev => ({
          ...prev,
          [orderId]: newReviews
        }));

        return {
          success: true,
          data: newReviews,
          message: data.message || "Review submitted successfully"
        };
      } else {
        throw new Error(data.message || "Failed to create review");
      }

    } catch (err) {
      console.error("Create review error:", err);

      let errorMessage = "Failed to submit review. Please try again.";

      if (err.message.includes("Authentication")) {
        errorMessage = "Session expired. Please login again.";
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);

      return {
        success: false,
        error: errorMessage
      };
    } finally {
      setLoading(false);
    }
  }, []);

  // Check if an order has a review
  const hasReview = useCallback((orderId) => {
    const reviews = orderReviews[orderId];
    const hasReviewData = reviews && reviews.length > 0;
    console.log(`Checking if order ${orderId} has review:`, hasReviewData, reviews);
    return hasReviewData;
  }, [orderReviews]);

  // Get review for a specific order
  const getReview = useCallback((orderId) => {
    const reviews = orderReviews[orderId];
    console.log(`Getting review for order ${orderId}:`, reviews);
    if (reviews && reviews.length > 0) {
      // Return the first review (assuming one review per order)
      return reviews[0];
    }
    return null;
  }, [orderReviews]);

  // Get all reviews for a specific order
  const getOrderReviews = useCallback((orderId) => {
    const reviews = orderReviews[orderId] || [];
    console.log(`Getting all reviews for order ${orderId}:`, reviews);
    return reviews;
  }, [orderReviews]);

  // Get all reviews across all orders
  const getAllReviews = useCallback(() => {
    const allReviews = Object.values(orderReviews).flat();
    console.log("Getting all reviews:", allReviews);
    return allReviews;
  }, [orderReviews]);

  // Check if reviews have been loaded for an order
  const areReviewsLoaded = useCallback((orderId) => {
    return orderReviews.hasOwnProperty(orderId);
  }, [orderReviews]);

  // Clear reviews for a specific order
  const clearOrderReviews = useCallback((orderId) => {
    setOrderReviews(prev => {
      const newState = { ...prev };
      delete newState[orderId];
      return newState;
    });
  }, []);

  // Clear all reviews
  const clearAllReviews = useCallback(() => {
    setOrderReviews({});
  }, []);

  const contextValue = {
    // State
    orderReviews,
    loading,
    error,

    // Actions
    fetchOrderReview,
    createReview,
    clearError,
    clearOrderReviews,
    clearAllReviews,

    // Getters
    hasReview,
    getReview,
    getOrderReviews,
    getAllReviews,
    areReviewsLoaded
  };

  return (
    <ReviewContext.Provider value={contextValue}>
      {children}
    </ReviewContext.Provider>
  );
};

export default CustomerReviewProvider;
